import styled from '@emotion/styled/macro';
import {
  Button,
  SvgIcon,
  SvgNames,
  Tooltip,
  Typography,
  tokens,
  Skeleton,
  Pill,
} from '@sunrun/experience-ui-components';
import { useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { SfdcEnv } from '@sunrun/sales-experience-shared';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { PipelineCard } from '../molecules/PipelineCard';
import { PipelineCardV2 } from '../molecules/PipelineCardV2/PipelineCardV2';
import { PipelineProspect } from 'providers/Pipeline';
import { useUserProfile } from 'providers/UserProfile';
import { formatCurrency } from 'helpers/formatHelpers';
import { OpenCustomerProfileOptions } from 'helpers/listViewHelpers';
import {
  stageShowsCommissionTotal,
  sumColumnCommissions,
} from 'helpers/pipelineCommissionHelpers';
import {
  CHECKOUT,
  CITY_INSPECTION,
  CITY_PERMITTING,
  DESIGN_APPROVAL,
  HOME_REVIEW,
  INSTALL_PREP,
  INSTALLATION,
  NEW_CUSTOMER,
  OFFER_CREATED,
  OPPTY_CREATED,
  APPT_SCHEDULED,
  APPT_COMPLETED,
  POWER_ON,
  ARC,
  UNKNOWN,
} from 'components/atoms/PipelineConsts';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';

const PIPELINE_COLUMN_WIDTH = 321;
const PIPELINE_CARD_WIDTH = 303;

type PipelineColumnProps = {
  isPipelineLoading: boolean;
  stage: string;
  data: PipelineProspect[];
  allowExpand: boolean;
  includeSalesRep: boolean;
  unReadChatsByContactId: Record<string, number>;
  showInstallScheduleModalAndGetUrl: (prospect: PipelineProspect) => void;
  showCommission: boolean;
  openCustomerProfile: (
    prospectId: string,
    options?: OpenCustomerProfileOptions,
  ) => void;
  splatBaseUrl: string;
  oneBaseUrl: string;
  salesforceBaseUrl: string;
  sfPartnerBaseUrl: string;
  sfdcEnv: SfdcEnv;
};

const PipelineColumn = ({
  isPipelineLoading,
  stage,
  data,
  allowExpand,
  includeSalesRep,
  unReadChatsByContactId,
  showInstallScheduleModalAndGetUrl,
  openCustomerProfile,
  showCommission,
  splatBaseUrl,
  oneBaseUrl,
  salesforceBaseUrl,
  sfPartnerBaseUrl,
  sfdcEnv,
}: PipelineColumnProps) => {
  const [expanded, setExpanded] = useState(false);
  const columnRef = useRef<HTMLDivElement>(null);
  const { userProfile } = useUserProfile();
  const pipelineV2StylingEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.PIPELINE_V2_STYLING,
  );
  const {
    allowJobCode,
    tooltip,
    tooltipTitle,
    dataTestIdColumn,
    dataTestIdTooltip,
    alwaysShowTooltip,
  } = useMemo(() => {
    let allowJobCode = false;
    let alwaysShowTooltip = false;
    let tooltipTitle = '';
    let tooltip = '';
    let dataTestIdColumn = '';
    let dataTestIdTooltip = '';

    switch (stage) {
      case NEW_CUSTOMER:
        tooltipTitle = 'Leads & Opportunities';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--new-customer';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--new-customer-tooltip';
        tooltip =
          'This column shows customers in the lead or opportunity stage who do not yet have an offer generated.';
        break;
      case OFFER_CREATED:
        tooltipTitle = 'Opportunities with Offers';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--offer-created';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--offer-created-tooltip';
        tooltip =
          "This column shows customers who have an opportunity and an offer generated, but haven't yet signed any offer. ";
        break;
      case OPPTY_CREATED:
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--oppty-created';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--oppty-created-tooltip';
        tooltip = 'Opportunity stage 1 - no appointment scheduled';
        alwaysShowTooltip = true;
        break;
      case APPT_SCHEDULED:
        dataTestIdColumn =
          'sales-ex-dashboard--pipeline-column--appt-scheduled';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--appt-scheduled-tooltip';
        tooltip = 'Opportunity stage 2 - appointment scheduled';
        alwaysShowTooltip = true;
        break;
      case APPT_COMPLETED:
        dataTestIdColumn =
          'sales-ex-dashboard--pipeline-column--appt-completed';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--appt-completed-tooltip';
        tooltip = 'Opportunity stage 3 - initial appointment complete';
        alwaysShowTooltip = true;
        break;
      case CHECKOUT:
        tooltipTitle = 'Signed Opportunities';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--checkout';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--checkout-tooltip';
        tooltip =
          'This column shows customers who have at least one signed offer (Opportunity stage 4. Signed thru 6. Submitted) and have not yet reached Sunrun Approval (Opportunity stage 7. Closed Won)';
        break;
      case HOME_REVIEW:
        tooltipTitle = 'Sunrun Approved and Site Survey';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--home-review';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--home-review-tooltip';
        tooltip =
          'This column shows customers in stages: Sunrun approval (Opportunity Stage 7), home review scheduled, and home review complete.';
        allowJobCode = true;
        break;
      case DESIGN_APPROVAL:
        tooltipTitle = 'CAP Process';
        dataTestIdColumn =
          'sales-ex-dashboard--pipeline-column--design-approval';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--design-approval-tooltip';
        tooltip =
          'This column shows customers with project tasks: Layout + SOW Reveiw, Complete CAP Package, and Complete Design Package.';
        allowJobCode = true;
        break;
      case CITY_PERMITTING:
        tooltipTitle = 'Building Permits';
        dataTestIdColumn =
          'sales-ex-dashboard--pipeline-column--city-permitting';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--city-permitting-tooltip';
        tooltip =
          'This column shows customers with project tasks: Submit Building Permit Application and Recieve and Process Building Permit';
        allowJobCode = true;
        break;
      case INSTALL_PREP:
        tooltipTitle = 'Schedule Installation';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--install-prep';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--install-prep-tooltip';
        tooltip =
          'This column shows customers with project task: Schedule Installation.';
        allowJobCode = true;
        break;
      case INSTALLATION:
        tooltipTitle = 'Installation Progress';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--installation';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--installation-tooltip';
        tooltip =
          'This column shows customers with project tasks: Start Installation and Complete Installation';
        allowJobCode = true;
        break;
      case CITY_INSPECTION:
        tooltipTitle = 'Building Permit';
        dataTestIdColumn =
          'sales-ex-dashboard--pipeline-column--city-inspection';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--city-inspection-tooltip';
        tooltip =
          'This column shows customers with project task: Record Final Inspection Signoff (ESO).';
        allowJobCode = true;
        break;
      case POWER_ON:
        tooltipTitle = 'PTO & System Activation';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--power-on';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--power-on-tooltip';
        tooltip =
          'This column shows customers with project tasks: Activation Call and Confirm System Production.';
        allowJobCode = true;
        break;
      case ARC:
        tooltipTitle = 'PTO, Closed Lost, & Cancelled';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--arc';
        dataTestIdTooltip = 'sales-ex-dashboard--pipeline-column--arc-tooltip';
        tooltip =
          'This column shows customers with: an open At Risk Customer case, in Opportunity stage 11. Closed Lost, and Opportunity Stage 9. Cancelled.';
        allowJobCode = true;
        break;
      case UNKNOWN:
        tooltipTitle = 'Unknown';
        dataTestIdColumn = 'sales-ex-dashboard--pipeline-column--unknown';
        dataTestIdTooltip =
          'sales-ex-dashboard--pipeline-column--unknown-tooltip';
        tooltip =
          "This column shows customers who don't seem to fit into any other category.";
        allowJobCode = true;
        break;
      default:
        allowJobCode = true;
        break;
    }

    return {
      allowJobCode,
      tooltip,
      dataTestIdColumn,
      dataTestIdTooltip,
      tooltipTitle,
      alwaysShowTooltip,
    };
  }, [stage]);

  const useFusionCommission = userProfile?.isFusion ?? false;
  const columnCommissionTotals = useMemo(() => {
    if (!showCommission || !stageShowsCommissionTotal(stage) || !data?.length) {
      return null;
    }
    return sumColumnCommissions(data, useFusionCommission);
  }, [showCommission, stage, data, useFusionCommission]);

  const CardComponent = pipelineV2StylingEnabled
    ? PipelineCardV2
    : PipelineCard;

  const renderPipelineCard = (prospect: PipelineProspect) => (
    <CardComponent
      key={prospect.key}
      splatBaseUrl={splatBaseUrl}
      oneBaseUrl={oneBaseUrl}
      salesforceBaseUrl={salesforceBaseUrl}
      sfPartnerBaseUrl={sfPartnerBaseUrl}
      sfdcEnv={sfdcEnv}
      showInstallScheduleModalAndGetUrl={showInstallScheduleModalAndGetUrl}
      openCustomerProfile={openCustomerProfile}
      pipelineProspect={prospect}
      includeSalesRep={includeSalesRep}
      allowJobCode={allowJobCode}
      numUnreadChats={
        prospect.contactId ? unReadChatsByContactId[prospect.contactId] : 0
      }
      showCommission={showCommission}
    />
  );

  return (
    <Column expanded={expanded} ref={columnRef} data-testid={dataTestIdColumn}>
      <ColumnHeader>
        <ColumnHeaderArea>
          <Typography>{stage}</Typography>
          {(expanded || alwaysShowTooltip) && (
            <StageInfoTooltip
              width={alwaysShowTooltip ? 180 : 240}
              customTestId={dataTestIdTooltip}
              content={
                <TooltipContainer>
                  {tooltipTitle && (
                    <Typography size={20} fontWeight="bold" component="span">
                      {tooltipTitle}
                      <br />
                    </Typography>
                  )}
                  <Typography component="span">{tooltip}</Typography>
                </TooltipContainer>
              }
              direction="bottom-left">
              <SvgIcon
                name={SvgNames.InfoOutline}
                width={STAGE_INFO_ICON_SIZE}
                height={STAGE_INFO_ICON_SIZE}
                color={tokens.TINTS_OFF_WHITE_20}
              />
            </StageInfoTooltip>
          )}
        </ColumnHeaderArea>
        <ColumnHeaderArea>
          {columnCommissionTotals !== null &&
            (useFusionCommission ? (
              <Tooltip
                customTestId={`${dataTestIdColumn}-commission-tooltip`}
                content={
                  <CommissionTooltipText component="span">
                    Total est. expert commission:{' '}
                    {formatCurrency(
                      columnCommissionTotals.fusionExpertTotal,
                      0,
                    )}
                    <br />
                    Total est. specialist commission:{' '}
                    {formatCurrency(
                      columnCommissionTotals.fusionSpecialistTotal,
                      0,
                    )}
                  </CommissionTooltipText>
                }
                direction="bottom-left">
                <CommissionTotalPill
                  text={formatCurrency(columnCommissionTotals.total, 0)}
                  type="success"
                  icon={SvgNames.InfoOutline}
                />
              </Tooltip>
            ) : (
              <Tooltip
                customTestId={`${dataTestIdColumn}-commission-tooltip`}
                content={
                  <CommissionTooltipText component="span">
                    Total estimated net commission:{' '}
                    {formatCurrency(columnCommissionTotals.total, 0)}
                  </CommissionTooltipText>
                }
                direction="bottom-left">
                <CommissionTotalPill
                  text={formatCurrency(columnCommissionTotals.total, 0)}
                  type="success"
                  icon={SvgNames.InfoOutline}
                />
              </Tooltip>
            ))}
          {data?.length > 0 && <Count>{data?.length}</Count>}
          {isPipelineLoading && <SkeletonBadge />}
          {allowExpand && (
            <ExpandButton
              iconMiddle={
                <SvgIcon
                  name={
                    expanded
                      ? SvgNames.ArrowsInSimple
                      : SvgNames.ArrowsOutSimple
                  }></SvgIcon>
              }
              color="tertiary"
              disabled={isPipelineLoading}
              onClick={() => {
                if (isPipelineLoading) return;
                setExpanded((value) => !value);
                if (columnRef.current) {
                  columnRef.current.scrollIntoView({
                    block: 'nearest',
                    inline: 'start',
                  });
                }
              }}></ExpandButton>
          )}
        </ColumnHeaderArea>
      </ColumnHeader>
      {isPipelineLoading && (
        <ColumnBody>
          <SkeletonBlock />
        </ColumnBody>
      )}
      {/* Virtuoso is used to render the pipeline cards in a virtual list and only works if the list is not expanded */}
      {data?.length > 0 && !expanded && (
        <Virtuoso
          components={{
            List: ColumnBody,
          }}
          computeItemKey={(
            _index: number,
            pipelineProspect: PipelineProspect,
          ) => pipelineProspect.key}
          itemContent={(_index: number, pipelineProspect: PipelineProspect) =>
            renderPipelineCard(pipelineProspect)
          }
          data={data}
        />
      )}
      {/* When the list is expanded, we render the pipeline cards in a grid */}
      {data?.length > 0 && expanded && (
        <ColumnBody>
          {data?.map((pipelineProspect) =>
            renderPipelineCard(pipelineProspect),
          )}
        </ColumnBody>
      )}
    </Column>
  );
};

const Column = styled.div<{ expanded: boolean }>`
  --numberOfColumns: calc((100vw - 95px) / ${PIPELINE_COLUMN_WIDTH});
  --calculatedSize: calc(
    round(down, var(--numberOfColumns), 1px) * ${PIPELINE_COLUMN_WIDTH}
  );
  flex: ${(props) =>
    props.expanded
      ? '0 0 var(--calculatedSize)'
      : `0 0 ${PIPELINE_COLUMN_WIDTH}px`};
  width: ${(props) =>
    props.expanded ? 'var(--calculatedSize)' : `${PIPELINE_COLUMN_WIDTH}px`};
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background-color: #1f26470a;
  padding: 0.75rem;
  border-radius: 10px;
  scroll-snap-align: start;
  scroll-snap-stop: always;
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.5rem;
`;

const ColumnHeaderArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipContainer = styled.div`
  overflow-wrap: break-word;
`;

// custom styling for the column tooltip specifically
const STAGE_INFO_ICON_SIZE = 14;
const STAGE_INFO_ARROW_OFFSET = 24;

const StageInfoTooltip = styled(Tooltip)`
  padding-top: 3px;
  && [role='tooltip'].bottom-left {
    left: ${STAGE_INFO_ICON_SIZE / 2 - STAGE_INFO_ARROW_OFFSET}px;
  }

  && [role='tooltip'].bottom-left::before {
    left: ${STAGE_INFO_ARROW_OFFSET}px;
  }
`;

const Count = styled.div`
  border-radius: 100px;
  border: 1px solid ${tokens.OFFWHITE_40};
  padding: 0 0.5rem;
  background: ${tokens.WHITE};
`;

const CommissionTotalPill = styled(Pill)`
  padding: 0.25rem 0.5rem !important;

  > p {
    line-height: normal;
    font-size: 12px;
  }
`;

const CommissionTooltipText = styled(Typography)`
  width: 275px;
  color: ${tokens.INFO_30};
`;

const ExpandButton = styled(Button)`
  padding: 0.5rem;
`;

const ColumnBody = styled.div`
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 1rem;
`;

const SkeletonBlock = styled(Skeleton)`
  height: 300px;
  width: 100%;
  margin-bottom: 10px;
`;

const SkeletonBadge = styled(Skeleton)`
  height: 20px;
  width: 32px;
  border-radius: 10px;
`;

export {
  PIPELINE_COLUMN_WIDTH,
  PIPELINE_CARD_WIDTH,
  PipelineColumn,
  Column as PipelineColumnShell,
  ColumnHeader as PipelineColumnHeader,
  ExpandButton as PipelineColumnExpandButton,
};
